import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FiSearch, FiPlus, FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import "./CreateUserGroupModal.css";
import { useSelector } from "react-redux";

const Domain = "https://estbn.com/EstbnNew/public/";
const USER_GROUP_API_URL = Domain + "api/portal/user-groups";
const TRAGET_ROLE_URL = Domain + "api/portal/targeting-rules";
const USERS_API_URL = Domain + "api/users";

const CreateUserGroupModal = ({ show, onClose, onGroupSaved, groupToEdit }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ugmGroupType, setUgmGroupType] = useState("");
  const [targetingRuleId, setTargetingRuleId] = useState("");
  const [fetchedTargetingRules, setFetchedTargetingRules] = useState([]);
  const [ugmTargetingRulesLoading, setUgmTargetingRulesLoading] =
    useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [ugmUsersLoading, setUgmUsersLoading] = useState(false);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      if (groupToEdit) {
        setName(groupToEdit.name || "");
        setDescription(groupToEdit.description || "");

        const initialUgmGroupType = groupToEdit.is_dynamic
          ? "dynamic"
          : "static";
        setUgmGroupType(initialUgmGroupType);
        setTargetingRuleId(groupToEdit.targeting_rule_id || "");

        if (
          initialUgmGroupType === "static" &&
          groupToEdit.user_ids &&
          Array.isArray(groupToEdit.user_ids)
        ) {
        } else {
          setSelectedUsers([]);
        }
      } else {
        setName("");
        setDescription("");
        setUgmGroupType("");
        setTargetingRuleId("");
        setSelectedUsers([]);
      }
      setSearchTerm("");
      setLoading(false);
      if (!groupToEdit) {
        setAvailableUsers([]);
        setSelectedUsers([]);
      }
    }
  }, [show, groupToEdit]);

  useEffect(() => {
    const fetchTargetingRules = async () => {
      setUgmTargetingRulesLoading(true);
      try {
        const response = await axios.get(TRAGET_ROLE_URL, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFetchedTargetingRules(response.data.data.targeting_rules.data);
      } catch (error) {
        console.error("Failed to fetch targeting rules:", error);
        toast.error("Failed to load targeting rules.");
      } finally {
        setUgmTargetingRulesLoading(false);
      }
    };

    if (show && ugmGroupType === "dynamic") {
      fetchTargetingRules();
    } else {
      setFetchedTargetingRules([]);
    }
  }, [show, ugmGroupType]);

  const fetchUsers = useCallback(async () => {
    setUgmUsersLoading(true);
    try {
      const response = await axios.get(USERS_API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fetchedAllUsers = response.data.data.users || [];
      setAvailableUsers(fetchedAllUsers);

      if (
        groupToEdit &&
        groupToEdit.user_ids &&
        Array.isArray(groupToEdit.user_ids) &&
        ugmGroupType === "static"
      ) {
        const initialSelected = fetchedAllUsers.filter((user) =>
          groupToEdit.user_ids.includes(user.id)
        );
        setSelectedUsers(initialSelected);
      } else {
        setSelectedUsers([]);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users for static ugm-group.");
      setAvailableUsers([]);
      setSelectedUsers([]);
    } finally {
      setUgmUsersLoading(false);
    }
  }, [groupToEdit, ugmGroupType]);

  useEffect(() => {
    if (show && ugmGroupType === "static") {
      fetchUsers();
    } else if (ugmGroupType !== "static" && show) {
      setAvailableUsers([]);
      setSelectedUsers([]);
    }
  }, [show, ugmGroupType, fetchUsers]);
  const { user } = useSelector((state) => state.auth);
  const token = user?.token;

  const filteredAvailableUsers = availableUsers.filter((user) => {
    const isSelected = selectedUsers.some(
      (selectedUser) => selectedUser.id === user.id
    );
    if (isSelected) return false;

    const userName = user.name ? user.name.toLowerCase() : "";
    const userEmail = user.email ? user.email.toLowerCase() : "";
    const searchLower = searchTerm.toLowerCase();

    return userName.includes(searchLower) || userEmail.includes(searchLower);
  });

  const handleAddUser = (userToAdd) => {
    setSelectedUsers((prev) => [...prev, userToAdd]);
  };

  const handleRemoveUser = (userToRemove) => {
    setSelectedUsers((prev) =>
      prev.filter((user) => user.id !== userToRemove.id)
    );
  };

  const getInitials = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const handleUgmGroupTypeChange = (newType) => {
    if (newType === "dynamic") {
      setSelectedUsers([]);
      setSearchTerm("");
    } else {
      setTargetingRuleId("");
    }
    setUgmGroupType(newType);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Please enter a user ugm-group name.");
      return;
    }
    if (!ugmGroupType) {
      toast.error("Please select a UgmGroup Type.");
      return;
    }

    let payload = {
      name: name,
      description: description,
    };

    if (ugmGroupType === "dynamic") {
      if (!targetingRuleId) {
        toast.error("Please select a targeting rule for dynamic ugm-groups.");
        return;
      }
      payload.is_dynamic = 1;
      payload.targeting_rule_id = targetingRuleId;
      payload.user_ids = []; // Ensure user_ids is empty for dynamic
    } else if (ugmGroupType === "static") {
      if (selectedUsers.length === 0) {
        toast.error("Please select at least one user for a static ugm-group.");
        return;
      }
      payload.is_dynamic = 0;
      payload.user_ids = selectedUsers.map((user) => user.id);
    }

    setLoading(true);
    try {
      const formData = new FormData();
      for (const key in payload) {
        if (Array.isArray(payload[key])) {
          payload[key].forEach((value) => {
            formData.append(`${key}[]`, value);
          });
        } else {
          formData.append(key, payload[key]);
        }
      }

      let response;
      if (groupToEdit) {
        formData.append("_method", "PUT"); // Laravel expects _method for PUT/PATCH via POST
        response = await axios.post(
          `${USER_GROUP_API_URL}/${groupToEdit.id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await axios.post(USER_GROUP_API_URL, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      }

      if (response.data.success) {
        toast.success(
          response.data.message ||
            `User ugm-group ${
              groupToEdit ? "updated" : "created"
            } successfully!`
        );
        onClose();
        if (onGroupSaved) {
          onGroupSaved();
        }
      } else {
        toast.error(
          response.data.message ||
            `Failed to ${groupToEdit ? "update" : "create"} user ugm-group.`
        );
      }
    } catch (error) {
      console.error("Error saving user ugm-group:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast.error(`Error: ${error.response.data.message}`);
      } else {
        toast.error("An error occurred while saving the user ugm-group.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div className="ugm-modal-overlay">
      <div className="ugm-modal-content">
        <div className="ugm-modal-header">
          <h2 className="ugm-modal-title">
            {groupToEdit ? "Edit User UgmGroup" : "Create New User UgmGroup"}
          </h2>
          <button
            className="ugm-modal-close-btn"
            onClick={onClose}
            disabled={loading}
            aria-label="Close ugm-modal"
          ></button>
        </div>
        <div className="ugm-modal-body">
          <div className="ugm-form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              className="ugm-form-control"
              placeholder="Add user ugm-group name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="ugm-form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              className="ugm-form-control ugm-textarea"
              placeholder="Enter user ugm-group Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={loading}
            ></textarea>
          </div>

          <div className="ugm-form-group">
            <label>UgmGroup Type</label>
            <div className="ugm-radio-group">
              <label>
                <div>
                  <input
                    type="radio"
                    name="ugmGroupType"
                    value="dynamic"
                    checked={ugmGroupType === "dynamic"}
                    onChange={() => handleUgmGroupTypeChange("dynamic")}
                    disabled={loading}
                  />
                  <span className="ugm-radio-label-text">Dynamic UgmGroup</span>
                </div>
                <span className="ugm-radio-description">
                  Users are automatically included based on targeting rules
                </span>
              </label>
              <label>
                <div>
                  <input
                    type="radio"
                    name="ugmGroupType"
                    value="static"
                    checked={ugmGroupType === "static"}
                    onChange={() => handleUgmGroupTypeChange("static")}
                    disabled={loading}
                  />
                  <span className="ugm-radio-label-text">Static UgmGroup</span>
                </div>
                <span className="ugm-radio-description">
                  Manually select specific users to include
                </span>
              </label>
            </div>
          </div>

          {ugmGroupType === "dynamic" && (
            <div className="ugm-form-group">
              <label htmlFor="targetingRule">
                Targeting Rule <span className="ugm-required">*</span>
              </label>
              <div className="ugm-select-wrapper">
                <select
                  id="targetingRule"
                  className="ugm-form-control"
                  value={targetingRuleId}
                  onChange={(e) => setTargetingRuleId(e.target.value)}
                  disabled={loading || ugmTargetingRulesLoading}
                  required
                >
                  <option value="">
                    {ugmTargetingRulesLoading
                      ? "Loading rules..."
                      : "Select targeting rule"}
                  </option>
                  {fetchedTargetingRules.map((rule) => (
                    <option key={rule.id} value={rule.id}>
                      {rule.name}
                    </option>
                  ))}
                  {!ugmTargetingRulesLoading &&
                    fetchedTargetingRules.length === 0 && (
                      <option value="" disabled>
                        No targeting rules found.
                      </option>
                    )}
                </select>
                <span className="ugm-select-arrow"></span>
              </div>
            </div>
          )}

          {ugmGroupType === "static" && (
            <div className="ugm-form-group ugm-selected-users-section">
              <div className="ugm-search-users-input-wrapper">
                <FiSearch />
                <input
                  type="text"
                  id="searchUsers"
                  placeholder={
                    ugmUsersLoading ? "Loading users..." : "Search users"
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={loading || ugmUsersLoading}
                />
              </div>

              <div className="ugm-user-lists-container">
                <div className="ugm-user-list-column">
                  <label className="ugm-column-label">Available Users</label>
                  {ugmUsersLoading ? (
                    <p className="ugm-loading-message">Loading users...</p>
                  ) : filteredAvailableUsers.length > 0 ? (
                    filteredAvailableUsers.map((user) => (
                      <div key={user.id} className="ugm-user-card">
                        <div className="ugm-user-avatar">
                          {getInitials(user.name)}
                        </div>
                        <div className="ugm-user-info">
                          <div className="ugm-user-name">
                            {user.name || "N/A"}
                          </div>
                          <div className="ugm-user-email">
                            {user.email || "N/A"}
                          </div>
                        </div>
                        <button
                          className="ugm-action-icon ugm-add-user"
                          onClick={() => handleAddUser(user)}
                          disabled={loading || ugmUsersLoading}
                          aria-label={`Add ${user.name || user.email}`}
                        >
                          <FiPlus />
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="ugm-no-users-message">
                      {searchTerm
                        ? "No matching users found."
                        : "No users to display."}
                    </p>
                  )}
                </div>

                <div className="ugm-user-list-column ugm-selected-users-column">
                  <label className="ugm-column-label">Selected Users</label>
                  {selectedUsers.length > 0 ? (
                    selectedUsers.map((user) => (
                      <div key={user.id} className="ugm-user-card">
                        <div className="ugm-user-avatar">
                          {getInitials(user.name)}
                        </div>
                        <div className="ugm-user-info">
                          <div className="ugm-user-name">
                            {user.name || "N/A"}
                          </div>
                          <div className="ugm-user-email">
                            {user.email || "N/A"}
                          </div>
                        </div>
                        <button
                          className="ugm-action-icon ugm-remove-user"
                          onClick={() => handleRemoveUser(user)}
                          disabled={loading}
                          aria-label={`Remove ${user.name || user.email}`}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="ugm-no-users-message">
                      No users selected yet.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="ugm-modal-footer">
          <button
            className="ugm-btn ugm-btn-cancel"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="ugm-btn ugm-btn-save"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : groupToEdit ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateUserGroupModal;
